const tokenRules = {
  accent: { property: '--coral', pattern: /^#[0-9a-f]{6}$/i },
  accentStrong: { property: '--coral-dark', pattern: /^#[0-9a-f]{6}$/i },
  ink: { property: '--ink', pattern: /^#[0-9a-f]{6}$/i },
  inkSecondary: { property: '--ink-2', pattern: /^#[0-9a-f]{6}$/i },
  paper: { property: '--paper', pattern: /^#[0-9a-f]{6}$/i },
  surface: { property: '--surface', pattern: /^#[0-9a-f]{6}$/i },
  muted: { property: '--muted', pattern: /^#[0-9a-f]{6}$/i },
  radius: { property: '--radius', pattern: /^(?:[0-9]|1[0-9]|2[0-4])px$/ },
}

export function sanitizeThemeTokens(theme) {
  if (typeof theme === 'string') {
    try { theme = JSON.parse(theme) } catch { return {} }
  }
  if (!theme || typeof theme !== 'object' || Array.isArray(theme)) return {}
  return Object.fromEntries(Object.entries(theme).flatMap(([key, value]) => {
    const rule = tokenRules[key]
    if (!rule || typeof value !== 'string' || !rule.pattern.test(value.trim())) return []
    return [[rule.property, value.trim()]]
  }))
}

export function applySessionTheme(theme, element = document.documentElement) {
  const safeTokens = sanitizeThemeTokens(theme)
  Object.values(tokenRules).forEach(({ property }) => element.style.removeProperty(property))
  Object.entries(safeTokens).forEach(([property, value]) => element.style.setProperty(property, value))
  return safeTokens
}

function balancedCssBlocks(css) {
  let depth = 0
  let quote = ''
  let comment = false
  for (let index = 0; index < css.length; index++) {
    const character = css[index]
    if (comment) { if (character === '*' && css[index + 1] === '/') { comment = false; index++ } continue }
    if (quote) { if (character === quote) quote = ''; continue }
    if (character === '/' && css[index + 1] === '*') { comment = true; index++; continue }
    if (character === "'" || character === '"') { quote = character; continue }
    if (character === '{') depth++
    if (character === '}' && --depth < 0) return false
  }
  return depth === 0 && !quote && !comment
}

export function sanitizeBrandingStyles(branding = {}) {
  const fontFamily = typeof branding.fontFamily === 'string' && branding.fontFamily.length <= 200
    && /^[a-zA-Z0-9 ,_'"-]+$/.test(branding.fontFamily) ? branding.fontFamily.trim() : ''
  const stylesheetUrls = (Array.isArray(branding.stylesheetUrls) ? branding.stylesheetUrls : []).slice(0, 4).flatMap(value => {
    if (typeof value !== 'string' || value.length > 2048 || /[\x00-\x1f\x7f]/.test(value)) return []
    try {
      const url = new URL(value)
      return url.protocol === 'https:' && !url.username && !url.password && !url.hash ? [url.href] : []
    } catch { return [] }
  })
  const candidate = typeof branding.inlineCss === 'string' ? branding.inlineCss.trim() : ''
  const inlineCss = candidate.length <= 16000 && balancedCssBlocks(candidate) && !/[<>\\]|@(?:import|charset|namespace)|url\s*\(|expression\s*\(|(?:behavior|-moz-binding)\s*:/i.test(candidate) ? candidate : ''
  return { fontFamily, stylesheetUrls: [...new Set(stylesheetUrls)], inlineCss }
}

// Brand resources exist only while viewing a public event surface. Never accept
// raw head HTML, scripts, or event handlers; only create explicit stylesheet links.
export function applyBrandingStyles(branding, enabled, doc = document) {
  const styles = enabled ? sanitizeBrandingStyles(branding || {}) : { fontFamily: '', stylesheetUrls: [], inlineCss: '' }
  const desired = new Set(styles.stylesheetUrls)
  doc.querySelectorAll('link[data-gather-brand]').forEach(node => {
    if (!desired.has(node.href)) node.remove()
    else desired.delete(node.href)
  })
  desired.forEach(href => {
    const link = doc.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    link.referrerPolicy = 'no-referrer'
    link.dataset.gatherBrand = 'stylesheet'
    doc.head.appendChild(link)
  })
  let inline = doc.querySelector('style[data-gather-brand]')
  if (!styles.inlineCss) inline?.remove()
  else {
    if (!inline) {
      inline = doc.createElement('style')
      inline.dataset.gatherBrand = 'inline'
      doc.head.appendChild(inline)
    }
    // Scope at the public shell's parent so .gather-event can style the shell
    // itself as well as its descendants. Scoping at .app--event excludes normal
    // class selectors for that scope root; clients should not need :scope hacks.
    inline.textContent = `@scope (#app:has(> .app--event)) { ${styles.inlineCss} }`
  }
  for (const property of ['--body-font', '--heading-font']) {
    if (styles.fontFamily) doc.documentElement.style.setProperty(property, styles.fontFamily)
    else doc.documentElement.style.removeProperty(property)
  }
  return styles
}
